import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Switch,
  FormControlLabel,
  FormGroup,
  Divider,
  Button,
  Alert,
  Chip,
  useMediaQuery,
  useTheme,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
} from '@mui/material';
import SettingsIcon from '@mui/icons-material/Settings';
import SaveIcon from '@mui/icons-material/Save';
import { useAuth } from '../context/AuthContext';
import { loadQualifierSettings, saveQualifierSettings } from '../services/qualifierSettingsService';

const DEFAULT_COLUMNS = {
  rank: false,
  firstName: true,
  lastName: true,
  club: true,
  coach: true,
  competitions: true,
  qualifiedBadge: true,
  totalPoints: false,
  top3Scores: false,
  bestScore: false,
  levelAdvancement: false,
};

const DEFAULT_SETTINGS = {
  columns: DEFAULT_COLUMNS,
  showRank: false,
  sortAlphabetically: true,
  showRunnersUp: true,
  runnersUpCount: 5,
};

export default function QualifierSettings({ open, onClose, onSave }) {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const { isAdmin } = useAuth();
  
  const [settings, setSettings] = useState(DEFAULT_SETTINGS);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);

  useEffect(() => {
    if (open) {
      loadSettings();
    }
  }, [open]);

  const loadSettings = async () => {
    try {
      setLoading(true);
      const loadedSettings = await loadQualifierSettings();
      setSettings(loadedSettings || DEFAULT_SETTINGS);
    } catch (err) {
      console.error('Error loading settings:', err);
      setError('Failed to load settings');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    try {
      console.log('🔍 QualifierSettings handleSave called:', {
        isAdmin,
        settings,
        localStorage: {
          adminToken: localStorage.getItem('adminToken'),
          hasAdminToken: !!localStorage.getItem('adminToken')
        }
      });
      
      setLoading(true);
      await saveQualifierSettings(settings);
      setSuccess(true);
      setTimeout(() => setSuccess(false), 3000);
    } catch (err) {
      console.error('Error saving settings:', err);
      setError('Failed to save settings');
    } finally {
      setLoading(false);
    }
  };

  const handleColumnToggle = (column) => {
    setSettings(prev => ({
      ...prev,
      columns: {
        ...prev.columns,
        [column]: !prev.columns[column]
      }
    }));
  };

  const handleSettingChange = (setting, value) => {
    setSettings(prev => ({
      ...prev,
      [setting]: value
    }));
  };

  if (!isAdmin) {
    return null;
  }

  return (
    <Dialog 
      open={open} 
      onClose={onClose}
      maxWidth="md"
      fullWidth
      fullScreen={isMobile}
    >
      <DialogTitle>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <SettingsIcon />
          Qualifier Page Settings
        </Box>
      </DialogTitle>
      
      <DialogContent>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}
        
        {success && (
          <Alert severity="success" sx={{ mb: 2 }}>
            Settings saved successfully!
          </Alert>
        )}

        <Box sx={{ mb: 3 }}>
          <Typography variant="h6" gutterBottom>
            Column Visibility (Public View)
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Configure which columns are visible to public users on the qualifiers page.
          </Typography>
          
          <FormGroup row={!isMobile}>
            {Object.entries(settings.columns).map(([column, visible]) => (
              <FormControlLabel
                key={column}
                control={
                  <Switch
                    checked={visible}
                    onChange={() => handleColumnToggle(column)}
                    size="small"
                  />
                }
                label={getColumnLabel(column)}
                sx={{ minWidth: isMobile ? '100%' : '200px' }}
              />
            ))}
          </FormGroup>
        </Box>

        <Divider sx={{ my: 3 }} />

        <Box sx={{ mb: 3 }}>
          <Typography variant="h6" gutterBottom>
            Display Options
          </Typography>
          
          <FormGroup>
            <FormControlLabel
              control={
                <Switch
                  checked={settings.showRank}
                  onChange={(e) => handleSettingChange('showRank', e.target.checked)}
                />
              }
              label="Show qualification rank (1st, 2nd, 3rd, etc.)"
            />
            
            <FormControlLabel
              control={
                <Switch
                  checked={settings.sortAlphabetically}
                  onChange={(e) => handleSettingChange('sortAlphabetically', e.target.checked)}
                />
              }
              label="Sort skaters alphabetically within each level (instead of by points)"
            />
          </FormGroup>
        </Box>

        <Divider sx={{ my: 3 }} />

        <Box sx={{ mb: 3 }}>
          <Typography variant="h6" gutterBottom>
            Admin View Options
          </Typography>
          
          <FormGroup>
            <FormControlLabel
              control={
                <Switch
                  checked={settings.showRunnersUp}
                  onChange={(e) => handleSettingChange('showRunnersUp', e.target.checked)}
                />
              }
              label="Show runners-up (skaters 13-17) for withdrawal management"
            />
          </FormGroup>
          
          {settings.showRunnersUp && (
            <Box sx={{ mt: 2, ml: 3 }}>
              <FormControl size="small" sx={{ minWidth: 120 }}>
                <InputLabel>Runners-up count</InputLabel>
                <Select
                  value={settings.runnersUpCount}
                  onChange={(e) => handleSettingChange('runnersUpCount', e.target.value)}
                  label="Runners-up count"
                >
                  <MenuItem value={3}>3 skaters</MenuItem>
                  <MenuItem value={5}>5 skaters</MenuItem>
                  <MenuItem value={10}>10 skaters</MenuItem>
                </Select>
              </FormControl>
            </Box>
          )}
        </Box>

        <Divider sx={{ my: 3 }} />

        <Box sx={{ mb: 3 }}>
          <Typography variant="h6" gutterBottom>
            Withdrawal Management
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Admins can mark skaters as withdrawn. Withdrawn skaters are hidden from public view
            and marked as withdrawn in admin view.
          </Typography>
          
          <Chip 
            label="Withdrawal management is available in the admin view of the qualifiers page"
            color="info"
            variant="outlined"
          />
        </Box>
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose} disabled={loading}>
          Cancel
        </Button>
        <Button 
          onClick={handleSave} 
          variant="contained" 
          startIcon={<SaveIcon />}
          disabled={loading}
        >
          Save Settings
        </Button>
      </DialogActions>
    </Dialog>
  );
}

function getColumnLabel(column) {
  const labels = {
    rank: 'Rank',
    firstName: 'First Name',
    lastName: 'Last Name',
    club: 'Club',
    coach: 'Coach',
    competitions: 'Competitions',
    qualifiedBadge: 'Qualified Badge',
    totalPoints: 'Total Points',
    top3Scores: 'Top 3 Scores',
    bestScore: 'Best Score',
    levelAdvancement: 'Level Advancement',
  };
  return labels[column] || column;
}
